import { Category } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { FormErrorMessage } from '@/components/ui/FormErrorMessage';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { CategoryFormData } from '@/pages/categories/hooks/useCategoryMutations';
import { mdiCheck, mdiClose, mdiTag } from '@mdi/js';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

interface CategoryFormProps {
  category?: Category | null;
  onSubmit: (data: CategoryFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const getFormValues = (category?: Category | null): CategoryFormData => ({
  name: category?.name || '',
  enabled: category?.enabled ?? true,
});

export const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = !!category;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    reset,
  } = useForm<CategoryFormData>({
    defaultValues: getFormValues(category),
    mode: 'onChange',
  });

  React.useEffect(() => {
    if (category) {
      reset(getFormValues(category));
    }
  }, [category, reset]);

  const isEnabled = watch('enabled');

  const handleFormSubmit = async (data: CategoryFormData) => {
    await onSubmit(data);
  };

  return (
    <div className='p-6'>
      <div className='flex items-center space-x-3 mb-6 pb-4 border-b border-grey-200'>
        <div className='w-12 h-12 bg-grey-100 rounded-full flex items-center justify-center'>
          <div className='w-6 h-6 text-yellow-600'>
            <svg viewBox='0 0 24 24' fill='currentColor'>
              <path d={mdiTag} />
            </svg>
          </div>
        </div>
        <div>
          <h3 className='text-lg font-semibold text-grey-900'>
            {isEditing ? 'Edit Category' : 'Create New Category'}
          </h3>
          <p className='text-sm text-grey-600'>
            {isEditing
              ? 'Update category information'
              : 'Add a new business category'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-6'>
        {/* Category Information */}
        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Category Information
          </h4>
          <div className='grid grid-cols-1 gap-4'>
            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Category Code *
              </label>
              <Controller
                name='name'
                control={control}
                rules={{
                  required: 'Category code is required',
                  minLength: {
                    value: 2,
                    message: 'Code must be at least 2 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Code must be at most 100 characters',
                  },
                  pattern: {
                    value: /^[a-z0-9_-]+$/,
                    message:
                      'Code must be lowercase with only letters, numbers, underscores, or hyphens',
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    onChange={e => field.onChange(e.target.value.toLowerCase())}
                    type='text'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-grey-300'
                    }`}
                    placeholder='e.g. cafe, coworking_space, hotel'
                  />
                )}
              />
              <p className='text-xs text-grey-500 mt-1'>
                Lowercase code matched with statics to display name and icon
              </p>
              <FormErrorMessage message={errors.name?.message} />
            </div>
          </div>
        </div>

        {/* Category Status - Only show in edit mode */}
        {isEditing && (
          <div
            className={`rounded-lg p-4 ${isEnabled ? 'bg-green-50' : 'bg-red-50'}`}
          >
            <h4 className='text-sm font-medium text-grey-700 mb-4'>
              Category Status
            </h4>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-grey-900'>
                  {isEnabled ? 'Category Enabled' : 'Category Disabled'}
                </p>
                <p className='text-xs text-grey-500 mt-1'>
                  {isEnabled
                    ? 'Category is visible and can be assigned to businesses'
                    : 'Category is hidden and cannot be assigned to new businesses'}
                </p>
              </div>
              <Controller
                name='enabled'
                control={control}
                render={({ field }) => (
                  <ToggleSwitch value={field.value} onChange={field.onChange} />
                )}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className='flex items-center justify-end space-x-3 pt-6 border-t border-grey-200'>
          <Button
            type='button'
            label='Cancel'
            icon={mdiClose}
            color='light'
            onClick={onCancel}
            disabled={loading}
          />
          <Button
            type='submit'
            label={isEditing ? 'Update Category' : 'Create Category'}
            icon={mdiCheck}
            color='primary'
            loading={loading}
            disabled={!isValid || (!isDirty && isEditing)}
          />
        </div>
      </form>
    </div>
  );
};
